import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Menu, theme, Select, Button } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  DashboardOutlined,
  TeamOutlined,
  HomeOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../hooks/useLanguage';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: t('menu.dashboard') },
    { key: '/users', icon: <TeamOutlined />, label: t('menu.users') },
    { key: '/buildings', icon: <HomeOutlined />, label: t('menu.buildings') },
    { key: '/requests', icon: <FileTextOutlined />, label: t('menu.requests') },
    { key: '/stats', icon: <BarChartOutlined />, label: t('menu.stats') },
    { key: '/settings', icon: <SettingOutlined />, label: t('menu.settings') },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        style={{ background: token.colorBgBase }}
      >
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {collapsed ? (
            <img src="/favicon.png" alt="Logo" style={{ height: 32 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/favicon.png" alt="Logo" style={{ height: 32 }} />
              <span>Valet Admin</span>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 16px', background: token.colorBgBase, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 20, cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="text"
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              title={t('theme.toggle')}
            />
            <Select
              value={language}
              onChange={setLanguage}
              options={[
                { value: 'pl', label: 'PL' },
                { value: 'en', label: 'EN' },
              ]}
              style={{ width: 60 }}
              size="small"
            />
            <Button 
              type="text" 
              icon={<LogoutOutlined />} 
              onClick={() => { logout(); navigate('/login'); }}
              title={t('common.logout')}
            />
          </div>
        </Header>
        <Content style={{ margin: 16, padding: 24, background: token.colorBgBase, borderRadius: token.borderRadiusLG, overflow: 'auto' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
